import { useEffect, useRef, useCallback, useState } from "react";

interface UseKeyboardScrollOptions {
  enabled?: boolean;
}

export const useKeyboardScroll = ({
  enabled = true,
}: UseKeyboardScrollOptions = {}) => {
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const initialViewportHeight = useRef(window.innerHeight);

  // Виявлення iOS пристроїв
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  // Визначення висоти клавіатури
  useEffect(() => {
    if (!enabled) return;

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = initialViewportHeight.current - currentHeight;

      // Якщо висота зменшилась більше ніж на 150px, вважаємо що з'явилась клавіатура
      if (heightDifference > 150) {
        setKeyboardHeight(heightDifference);
      } else {
        setKeyboardHeight(0);
      }
    };

    // Підтримка Visual Viewport API для більш точного визначення клавіатури
    const handleVisualViewportChange = () => {
      const visualViewport = (window as any).visualViewport;
      if (visualViewport) {
        const viewportHeight = visualViewport.height;
        const windowHeight = window.innerHeight;
        const heightDifference = windowHeight - viewportHeight;

        if (heightDifference > 150) {
          setKeyboardHeight(heightDifference);
        } else {
          setKeyboardHeight(0);
        }
      }
    };

    window.addEventListener("resize", handleResize);

    // Додаємо слухач для Visual Viewport API якщо доступний
    const visualViewport = (window as any).visualViewport;
    if (visualViewport) {
      visualViewport.addEventListener("resize", handleVisualViewportChange);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (visualViewport) {
        visualViewport.removeEventListener(
          "resize",
          handleVisualViewportChange
        );
      }
    };
  }, [enabled]);

  // Позиціонування інпуту над клавіатурою
  const centerElementAboveKeyboard = useCallback(
    (element: HTMLElement) => {
      if (!element) return;

      const elementRect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - keyboardHeight;

      // Якщо клавіатура відкрита, позиціонуємо інпут ближче до клавіатури
      if (keyboardHeight > 0) {
        let targetY;

        if (isIOS) {
          // Для iOS: позиціонуємо інпут дуже близько до клавіатури
          // Використовуємо тільки 10% відступ від верху доступного простору
          targetY = elementRect.top + window.scrollY - availableHeight * 0.1;
        } else {
          // Для інших пристроїв: використовуємо 20% відступ
          targetY = elementRect.top + window.scrollY - availableHeight * 0.2;
        }

        const maxScrollY =
          document.documentElement.scrollHeight - viewportHeight;
        const finalScrollY = Math.min(Math.max(targetY, 0), maxScrollY);

        window.scrollTo({
          top: finalScrollY,
          behavior: "smooth",
        });
      } else {
        // Якщо клавіатура закрита, центруємо елемент
        const elementCenter =
          elementRect.top + window.scrollY + elementRect.height / 2;
        const targetY = elementCenter - viewportHeight / 2;
        const maxScrollY =
          document.documentElement.scrollHeight - viewportHeight;
        const finalScrollY = Math.min(Math.max(targetY, 0), maxScrollY);

        window.scrollTo({
          top: finalScrollY,
          behavior: "smooth",
        });
      }
    },
    [keyboardHeight, isIOS]
  );

  // Обробник фокусу з урахуванням клавіатури
  useEffect(() => {
    if (!enabled) return;

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
      ) {
        if (isIOS) {
          // Для iOS використовуємо scrollIntoView з більшою затримкою
          setTimeout(() => {
            target.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
          }, 500);
        } else {
          // Для інших пристроїв використовуємо нашу логіку
          setTimeout(() => {
            centerElementAboveKeyboard(target);
          }, 300);
        }
      }
    };

    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [enabled, centerElementAboveKeyboard, isIOS]);

  const scrollToInput = useCallback(
    (inputRef: React.RefObject<HTMLElement | null>) => {
      if (!inputRef.current) return;
      centerElementAboveKeyboard(inputRef.current);
    },
    [centerElementAboveKeyboard]
  );

  const focusAndCenterInput = useCallback(
    (inputRef: React.RefObject<HTMLElement | null>) => {
      if (!inputRef.current) return;
      inputRef.current.focus({ preventScroll: true });

      if (isIOS) {
        // Для iOS використовуємо scrollIntoView
        scrollTimeoutRef.current = setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
          }
        }, 500);
      } else {
        // Для інших пристроїв використовуємо нашу логіку
        scrollTimeoutRef.current = setTimeout(() => {
          scrollToInput(inputRef);
        }, 300);
      }
    },
    [scrollToInput, isIOS]
  );

  return {
    scrollToInput,
    focusAndCenterInput,
    centerElementAboveKeyboard,
  };
};
